import React from "react";
import { Box } from "@chakra-ui/react";
import { ExperimentFlags } from "@shared/utils.experimentation";
import type { ProductCardProduct, VariantType } from "@shared/graphql.types";
import {
  useBasketBuckets,
  useFeatureFlag,
  useHasCurrentMembership,
  useReferralRole
} from "hooks";

import { RafDiscountProgressBar } from "packages/acquisition/components/ReferAFriend/RafDiscountProgressBar";
import { MembershipList } from "components/BasketLists/MembershipList";
import { useQuizLink } from "hooks/useQuizLink";
import { QuizLink } from "basket_and_checkout/shared/components/ATBConfirmation/QuizLink";
import { wasMembershipAddedToBasket } from "lib/apollo/variables";
import { ThresholdCarousel } from "packages/basket-and-checkout/pages/basket/components/Thresholds/ThresholdCarousel/ThresholdCarousel";
import { atbBannerWrapper } from "packages/basket-and-checkout/pages/basket/components/Thresholds/ProgressBanner/ProgressBanner.css";
import { ThresholdTermsAndConditions } from "packages/basket-and-checkout/pages/basket/components/Thresholds/TermsAndConditions/TermsAndConditions";
import { AddPromoCodeBox } from "packages/acquisition/components/PromoCodes/AddPromoCodeBox/AddPromoCodeBox";
import { promoCodeExperimentValues } from "components/Basket/components/PromoCodes/constants";
import { THRESHOLDS_SECTION_FRAGMENT } from "lib/apollo/fragments";
import { getThresholds } from "packages/basket-and-checkout/pages/basket/components/Thresholds/thresholds";
import { useFragment } from "@apollo/client";
import {
  freeShippingTandCsWrapper,
  verticalMargins
} from "./ATBConfirmation.css";
import { ATBConfirmationProducts } from "./ATBConfirmationProducts";
import { SimpleProductCardWrapper } from "../ProductCard/SimpleProductCard/SimpleProductCardWrapper";
import { ATBConfirmationRecommendations } from "packages/ui-shared/components/RecommendationModule/ATBConfirmationRecommendations/ATBConfirmationRecommendations";
import { MembershipDetailsWrapper } from "packages/basket-and-checkout/shared/components/ATBConfirmation/MembershipDetails/MembershipDetailsWrapper";
import { useNMCustomerJourney } from "packages/acquisition/components/GuestPrice/hooks/useNMCustomerJourney";
import { MembershipToggleCard } from "../Membership/MembershipToggleCard/MembershipToggleCard";
import { confirmationPageItem } from "./ConfirmationPage.css";

type Props = {
  variant?: VariantType | ProductCardProduct | { sku: string };
  variants?: VariantType[];
  showFreeTrial: boolean;
};

export const ConfirmationPage: React.FC<Props> = ({
  variant,
  variants,
  showFreeTrial
}) => {
  const { inLimitItems } = useBasketBuckets();

  const currentProduct =
    variant &&
    inLimitItems.find(
      (item) => item.type === "product" && item?.variant?.sku === variant?.sku
    );
  const showRafComponents = !!useReferralRole();
  const isMember = useHasCurrentMembership();
  const { showQuizLink } = useQuizLink();
  const [promoCodeLoading, promoFlagExperimentValue] = useFeatureFlag(
    ExperimentFlags.USE_PROMO_CODE_EXPERIMENT,
    promoCodeExperimentValues.throwaway
  );

  const isPromoBoxEnabled =
    !promoCodeLoading &&
    promoFlagExperimentValue === promoCodeExperimentValues.promo_box;

  const isNMCustomerJourney = useNMCustomerJourney();

  const { data } = useFragment({
    fragment: THRESHOLDS_SECTION_FRAGMENT,
    from: "ThresholdsSection:ThresholdSection",
    fragmentName: "ThresholdsSectionFragment"
  });

  const thresholds = getThresholds({ items: data?.items });
  return (
    <Box
      mt={1}
      px={{ base: 1, md: 7, lg: 2 }}
      display="flex"
      flexDirection="column"
    >
      <div>
        {!showRafComponents && thresholds?.length ? (
          <ThresholdCarousel
            thresholds={thresholds}
            progressBannerStyles={atbBannerWrapper}
          />
        ) : null}
        {showRafComponents && <RafDiscountProgressBar />}
      </div>
      {!isMember && isPromoBoxEnabled && (
        <AddPromoCodeBox className={verticalMargins} />
      )}
      {!isMember && showFreeTrial ? (
        <div className={confirmationPageItem}>
          <MembershipToggleCard />
        </div>
      ) : (
        showQuizLink &&
        !isNMCustomerJourney && (
          <QuizLink variant="atbConfirmation" locationUrl="atb-confirmation" />
        )
      )}

      {variants && (
        <ATBConfirmationProducts
          variants={variants}
          inLimitItems={inLimitItems}
        />
      )}
      {!variants && currentProduct && (
        <SimpleProductCardWrapper item={currentProduct} />
      )}
      {!showFreeTrial && wasMembershipAddedToBasket() && (
        <>
          <MembershipList variant="atbConfirmation" />
          <MembershipDetailsWrapper variant="atbConfirmation" />
        </>
      )}
      <ATBConfirmationRecommendations />
      {thresholds?.length ? (
        <div className={freeShippingTandCsWrapper}>
          <ThresholdTermsAndConditions thresholds={thresholds} />
        </div>
      ) : null}
    </Box>
  );
};
